/**
 * Hexio App Engine Function extensions base library.
 *
 * @package hae-ext-functions-base
 * @copyright 2021 Hexio a.s. <contact@hexio.io> (hexio.io)
 * @license Commercial
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BP, declareFunction, SCHEMA_CONST_ANY_VALUE_TYPE, Type } from "@hexio_io/hae-lib-blueprint";
import { DateTime } from "luxon";

const formatinputDate = (inputDate) => {
	if (typeof inputDate === "number") {
		return DateTime.fromSeconds(inputDate);
	} else if (typeof inputDate === "string") {
		return DateTime.fromISO(inputDate);
	} else if (inputDate instanceof Date) {
		return DateTime.fromJSDate(inputDate);
	} else if (inputDate instanceof Object) {
		return DateTime.fromObject(inputDate);
	}
	return null
}

export const dateFormatFunc = declareFunction({
	name: "DATE_FORMAT",
	category: "date",
	label: "Format date",
	description: "Formats given date with a custom format.",
	argRequiredCount: 2,
	argSchemas: [
		BP.Any({
			label: "Date",
			constraints: {
				required: true
			},
			fallbackValue: 0,
			defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER
		}),
		BP.String({
			label: "Format",
			constraints: {
				required: true
			},
			fallbackValue: "i"
		})
	],
	argRestSchema: null,
	returnType: Type.String({
		label: "Formatted value"
	}),
	render: (_rCtx, args) => {
		const inputDate = args[0]();
		const format = args[1]();

		const dt: DateTime = formatinputDate(inputDate);

		if(dt) {
			return dt.toFormat(format);
		}else {
			throw new Error("Unsupported input date type.");
		}

	},
});

export const dateNow = declareFunction({
	name: "DATE_NOW",
	category: "date",
	label: "Current date",
	description: "Get current date as ISO-8601 string.",
	argRequiredCount: 0,
	argSchemas: [],
	argRestSchema: null,
	returnType: Type.String({
		label: "ISO-8601 date string"
	}),
	render: () => {
		return (new Date()).toISOString().split("T")[0];
	},
});

export const dateDiff = declareFunction({
	name: "DATE_DIFF",
	category: "date",
	label: "Date difference",
	description: "Get a difference betwen a two date",
	argRequiredCount: 2,
	argSchemas: [
		BP.Any({
			label: "Date 1",
			constraints: {
				required: true
			},
			fallbackValue: 0,
			defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER
		}),
		BP.Any({
			label: "Date 2",
			constraints: {
				required: true
			},
			fallbackValue: 0,
			defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER
		}),
		BP.String({
			label: "Format",
			constraints: {
				required: false
			},
			fallbackValue: "seconds"
		})
	],
	argRestSchema: null,
	returnType: Type.Any({
		label: "Difference between two date"
	}),
	render: (_rCtx, args) => {

		const inputDate1 = args[0]();
		const inputDate2 = args[1]();
		const format = args[2]() ?? "seconds";

		const date_1 = formatinputDate(inputDate1);
		const date_2 = formatinputDate(inputDate2);

		if(date_1 && date_2) {
			return date_1.diff(date_2, format).toObject();
		}else {
			throw new Error("Unsupported input date type.");
		}
	},
});
